import type { EventMarketDetail, EventMarketsResponse } from '../../api/types';
import { SOURCES } from './capabilities';
import type { EventKind, EventModel, Outcome, Quote } from './types';

function parseDate(value: string | null): Date | null {
    if (!value) return null;
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
}

function yesProbability(market: EventMarketDetail): number | null {
    if (market.current_yes_prob != null) return market.current_yes_prob;
    const first = market.outcome_prices?.[0];
    return typeof first === 'number' ? first : null;
}

function outcomeLabel(market: EventMarketDetail, isMulti: boolean): string {
    if (isMulti && market.yes_outcome) return market.yes_outcome;
    return market.question;
}

function eventKind(res: EventMarketsResponse): EventKind {
    if (res.markets.length <= 1) return 'binary';
    return res.neg_risk ? 'categories' : 'independent';
}

function toOutcome(
    market: EventMarketDetail,
    order: number,
    isMulti: boolean
): Outcome {
    const quote: Quote = {
        outcomeId: market.condition_id,
        source: 'polymarket',
        market: { source: 'polymarket', marketId: market.condition_id },
        probability: yesProbability(market),
        volume: market.volume,
        closed: market.closed === true,
        startDate: parseDate(market.start_date),
        endDate: parseDate(market.end_date),
    };
    return {
        id: market.condition_id,
        label: outcomeLabel(market, isMulti),
        question: market.question,
        value: null,
        order,
        image: market.image,
        quotes: [quote],
    };
}

export function eventFromPolymarket(res: EventMarketsResponse): EventModel {
    const isMulti = res.markets.length > 1;
    const outcomes = res.markets.map((m, i) => toOutcome(m, i, isMulti));
    const allClosed =
        outcomes.length > 0 &&
        outcomes.every(o => o.quotes.every(q => q.closed));
    return {
        id: res.event_id,
        slug: res.slug,
        title: res.event_title ?? res.markets[0]?.question ?? '',
        description: res.description,
        image: res.event_image,
        startDate: parseDate(res.start_date),
        endDate: parseDate(res.end_date),
        status: allClosed ? 'closed' : 'open',
        kind: eventKind(res),
        tags: res.tags ?? [],
        totalVolume: res.total_volume,
        sources: [SOURCES.polymarket],
        outcomes,
    };
}
